import { useEffect } from 'react';
import { useSEO } from '@/hooks/useSEO';
import Footer from '@/components/feature/Footer';
import { trackEvent } from './lib/tracking';
import { WHATSAPP_BASE, WHATSAPP_GENERIC_MESSAGE } from './lib/constants';
import LeanHeaderDiv from './components/LeanHeader';
import MobileCtaBar from './components/MobileCtaBar';
import HeroSection from './components/HeroSection';
import PorQueSection from './components/PorQueSection';
import SolucoesSection from './components/SolucoesSection';
import ModulacoesSection from './components/ModulacoesSection';
import DetalhesPremium from './components/DetalhesPremium';
import AplicacoesSection from './components/AplicacoesSection';
import ProjetoCompleto from './components/ProjetoCompleto';
import QualificacaoForm from './components/QualificacaoForm';
import FaqSection, { faqItems } from './components/FaqSection';
import CuradoriaGaleria from '@/components/feature/CuradoriaGaleria';
import { divisoriasAcustica } from '@/mocks/curadoria';

export default function DivisoriasPage() {
  useSEO({
    title: 'Divisórias Corporativas | Vidro, MDF e Soluções Modulares',
    description:
      'Projeto, fornecimento e instalação de divisórias corporativas em vidro, MDF, soluções mistas, acústicas e vidro duplo para empresas.',
    keywords:
      'divisórias corporativas, divisórias para escritório, divisória de vidro para escritório, divisórias em MDF, divisória modular, divisória acústica',
    path: '/divisorias',
  });

  useEffect(() => {
    let fired50 = false;
    let fired90 = false;
    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return;
      const pct = (window.scrollY / total) * 100;
      if (!fired50 && pct >= 50) { fired50 = true; trackEvent('scroll_50'); }
      if (!fired90 && pct >= 90) { fired90 = true; trackEvent('scroll_90'); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const baseUrl = import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com';
    const pageUrl = `${baseUrl}/divisorias`;

    const schemas: Array<{ id: string; data: Record<string, unknown> }> = [
      {
        id: 'schema-div-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Início', item: baseUrl },
            { '@type': 'ListItem', position: 2, name: 'Soluções', item: `${baseUrl}/produtos` },
            { '@type': 'ListItem', position: 3, name: 'Divisórias Corporativas', item: pageUrl },
          ],
        },
      },
      {
        id: 'schema-div-service',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Divisórias Corporativas',
          serviceType: 'Projeto, fornecimento e instalação de divisórias corporativas',
          description:
            'Projeto, fornecimento e instalação de divisórias corporativas em vidro, MDF, soluções mistas, acústicas e vidro duplo para empresas.',
          url: pageUrl,
          areaServed: ['Pernambuco', 'Nordeste'],
          provider: {
            '@type': 'LocalBusiness',
            name: 'Home Office Premium',
            telephone: '+55-81-98203-0285',
            url: baseUrl,
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Av. Agamenon Magalhães, 1519',
              addressLocality: 'Caruaru',
              addressRegion: 'PE',
              postalCode: '55014-000',
              addressCountry: 'BR',
            },
          },
        },
      },
      {
        id: 'schema-div-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        },
      },
    ];

    const scripts: HTMLScriptElement[] = [];
    schemas.forEach(({ id, data }) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
      scripts.push(script);
    });

    return () => { scripts.forEach((s) => s?.remove()); };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <LeanHeaderDiv />

      <HeroSection />
      <PorQueSection />
      <SolucoesSection />
      <ModulacoesSection />
      <DetalhesPremium />
      <AplicacoesSection />

      <CuradoriaGaleria
        eyebrow="Curadoria de fabricante · Gabbinetto"
        titulo="Vidro, madeira e alumínio no detalhe."
        descricao="Vidro simples, vidro duplo, divisória mista, portas e sistema de junta seca. Modelo, aplicação e página de origem identificados em cada item."
        produtos={divisoriasAcustica.slice(0, 5)}
        contexto="divisorias"
        tom="white"
      />

      <ProjetoCompleto />

      {/* Seção investimento */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="max-w-3xl mx-auto bg-background-50 border border-background-200/70 rounded-3xl p-8 md:p-14 text-center">
            <div className="inline-flex items-center gap-3 bg-white border border-background-200/70 rounded-full px-5 py-2.5 mb-7">
              <i className="ri-arrow-right-up-line text-primary-500 text-lg"></i>
              <span className="text-foreground-800 text-[14px] font-medium">
                Atendimento prioritário para projetos{' '}
                <span className="text-primary-600 font-semibold">a partir de R$ 50 mil</span>
              </span>
            </div>
            <h2 className="text-foreground-950 text-[24px] md:text-[32px] font-semibold tracking-tight leading-tight mb-5">
              Soluções para projetos corporativos de maior porte.
            </h2>
            <p className="text-foreground-600 text-[14px] md:text-[16px] leading-relaxed max-w-2xl mx-auto">
              O investimento depende da quantidade de ambientes, sistemas especificados, acabamentos
              e serviços envolvidos. Nosso foco são empresas que buscam uma solução profissional e
              estruturada — não apenas a compra avulsa de um painel.
            </p>
          </div>
        </div>
      </section>

      {/* Seção de qualificação do lead */}
      <section id="analise" className="bg-background-50 py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-2">
              <p className="text-[12px] md:text-[13px] font-semibold tracking-[0.28em] uppercase text-primary-500 mb-4">
                Análise do projeto
              </p>
              <h2 className="text-foreground-950 text-[28px] md:text-[40px] font-semibold tracking-tight leading-tight mb-6">
                Conte para a gente o seu projeto de divisórias.
              </h2>
              <p className="text-foreground-500 text-[15px] md:text-[16px] leading-relaxed">
                Responda algumas perguntas rápidas e continue o atendimento direto no WhatsApp —
                suas respostas já seguem preenchidas na mensagem para agilizar a análise.
              </p>
            </div>

            <div className="lg:col-span-3">
              <QualificacaoForm />
            </div>
          </div>
        </div>
      </section>

      <FaqSection />
      <Footer />

      {/* Barra CTA mobile */}
      <MobileCtaBar />

      {/* WhatsApp float (desktop) */}
      <a
        href={`${WHATSAPP_BASE}?text=${WHATSAPP_GENERIC_MESSAGE}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent('whatsapp_divisorias', { location: 'float' })}
        className="fixed bottom-5 right-5 z-[9999] hidden md:flex w-12 h-12 md:w-14 md:h-14 items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all duration-300 cursor-pointer shadow-[0_2px_12px_rgba(0,0,0,0.12)]"
        aria-label="Falar com a Home Office Premium no WhatsApp"
      >
        <i className="ri-whatsapp-line text-xl md:text-2xl"></i>
      </a>
    </div>
  );
}